// ─── Groundwork vocabulary — the single source for user-facing lifecycle & device terms ───
//
// One module answers "what do we call this?" for job statuses, lifecycle
// stages, backflow assembly types, and the estimate approval gate. Before this
// file existed the app carried FIVE hand-maintained copies of the status/stage
// vocabulary (client lifecycle.ts, jobDetailHelpers.ts ×2, jobs/index.tsx,
// and an inline SQL CASE in src/server/routes/jobs.ts) that had already
// drifted apart — held jobs rendered as "New Request", unqueued fell to
// intake, and the filter dropdown silently omitted five statuses.
//
// PURITY RULE: this module must stay free of imports, JSX, and "@/" aliases.
// The server-side parity test (src/server/services/vocabularyParity.test.ts)
// imports this file directly across the client/server project boundary, which
// only works while the module has no dependency graph of its own.

// ─── Job statuses ────────────────────────────────────────────────────────
// Mirror of src/server/services/jobStatusMachine.ts JOB_STATUSES — the server
// is the authority; the parity test fails the unit suite if the sets drift.

export const JOB_STATUSES = [
    "draft",
    "awaiting_scheduling",
    "unqueued",
    "scheduled",
    "en_route",
    "arrived",
    "in_progress",
    "office_review",
    "cannot_complete",
    "held",
    "completed",
    "closed",
    "cancelled",
] as const;

export type JobStatus = (typeof JOB_STATUSES)[number];

/** Statuses no longer minted but still present on historical rows. */
export const LEGACY_JOB_STATUSES = ["ready_for_invoice", "pending_action"] as const;

/** Plain-English label per status — never show snake_case to a human. */
export const STATUS_LABEL: Record<string, string> = {
    draft: "Draft",
    awaiting_scheduling: "Awaiting scheduling",
    unqueued: "Unqueued",
    scheduled: "Scheduled",
    en_route: "En route",
    arrived: "Arrived",
    in_progress: "In progress",
    office_review: "Office review",
    cannot_complete: "Cannot complete",
    held: "Held",
    completed: "Completed",
    closed: "Closed",
    cancelled: "Cancelled",
    ready_for_invoice: "Ready for invoice",
    pending_action: "Pending action",
};

export function statusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    return STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

/**
 * The NEW job page's words for a status (Corbin, 2026-10-09): the office calls
 * `office_review` "Closeout", so the new page's header, steps, status chip and
 * Move to ▾ say Closeout. The DB status keeps its name, and the classic page
 * and the rest of the app keep STATUS_LABEL until they move over too.
 */
export const JOB_PAGE_STATUS_LABEL: Record<string, string> = { ...STATUS_LABEL, office_review: "Closeout" };

export function jobPageStatusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    return JOB_PAGE_STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

/** The new job page's Move to ▾ words for the move into `office_review`. */
export const JOB_PAGE_TO_CLOSEOUT_LABEL = "Send to Closeout";

// ─── Lifecycle stages ────────────────────────────────────────────────────

export type LifecycleStageId =
    | "intake"
    | "ready_to_schedule"
    | "scheduled"
    | "in_progress"
    | "needs_review"
    | "completed"
    | "cancelled"
    | "at_risk"
    | "outstanding";

/**
 * Job status → lifecycle stage. Mirrored by the server's
 * LIFECYCLE_STAGE_OF_STATUS (jobStatusMachine.ts) so the Jobs page Lifecycle
 * view buckets rows exactly the way the client renders them — parity-tested.
 */
export const STAGE_OF_STATUS: Record<string, LifecycleStageId> = {
    draft: "intake",
    awaiting_scheduling: "ready_to_schedule",
    unqueued: "ready_to_schedule",
    scheduled: "scheduled",
    en_route: "scheduled",
    arrived: "in_progress",
    in_progress: "in_progress",
    office_review: "needs_review",
    cannot_complete: "needs_review",
    held: "needs_review",
    ready_for_invoice: "needs_review",
    pending_action: "needs_review",
    completed: "completed",
    closed: "completed",
    cancelled: "cancelled",
};

export function stageOfStatus(status: string | null | undefined): LifecycleStageId {
    const s = (status ?? "").toLowerCase().trim();
    return STAGE_OF_STATUS[s] ?? "intake";
}

/** Inverse view, derived so the two directions can never disagree. */
export const STAGE_STATUS_GROUPS: Record<LifecycleStageId, string[]> = (() => {
    const groups: Record<LifecycleStageId, string[]> = {
        intake: [],
        ready_to_schedule: [],
        scheduled: [],
        in_progress: [],
        needs_review: [],
        completed: [],
        cancelled: [],
        at_risk: [],
        outstanding: [],
    };
    for (const [status, stage] of Object.entries(STAGE_OF_STATUS)) groups[stage].push(status);
    return groups;
})();

// ─── Office status transitions ───────────────────────────────────────────
// The moves the office UI OFFERS — deliberately narrower than the server's
// full transition matrix (jobStatusMachine.ts TRANSITIONS), which also admits
// recovery edges the UI shouldn't advertise as buttons. The parity test
// asserts every entry here is legal server-side, so the UI can never offer a
// move the server would reject.

export const OFFICE_STATUS_TRANSITIONS: Record<string, string[]> = {
    draft: ["awaiting_scheduling", "scheduled"],
    awaiting_scheduling: ["scheduled", "held"],
    // Pre-vocabulary, `unqueued` had no entry at all — an unqueued job's
    // detail page offered zero status moves. These two are its legal exits.
    unqueued: ["awaiting_scheduling", "scheduled"],
    scheduled: ["awaiting_scheduling", "en_route", "arrived", "in_progress", "held"],
    en_route: ["arrived", "in_progress", "scheduled", "cannot_complete"],
    arrived: ["in_progress", "en_route", "cannot_complete"],
    in_progress: ["office_review", "cannot_complete", "held"],
    office_review: ["held", "in_progress"],
    cannot_complete: ["office_review", "in_progress", "scheduled", "held"],
    held: ["in_progress", "office_review", "scheduled"],
    completed: ["office_review"],
    closed: [],
    cancelled: [],
};

export const TRANSITION_LABELS: Record<string, string> = {
    awaiting_scheduling: "Move to scheduling",
    scheduled: "Schedule",
    en_route: "Mark en route",
    arrived: "Mark arrived",
    in_progress: "Start",
    office_review: "Send to office review",
    cannot_complete: "Mark cannot complete",
    held: "Hold",
    completed: "Complete",
};

// ─── Backflow assembly types ─────────────────────────────────────────────

export interface AssemblyVocabEntry {
    /** The industry short code everyone writes on paperwork ("DC", "RPZ"). */
    short: string;
    /** The plain-English name a new hire can actually learn from. */
    name: string;
}

/** Every assembly_type value the DB enum admits, including legacy ones. */
export const ASSEMBLY_VOCAB: Record<string, AssemblyVocabEntry> = {
    rpz: { short: "RPZ", name: "Reduced Pressure Zone" },
    dc: { short: "DC", name: "Double Check" },
    dcva: { short: "DCVA", name: "Double Check Valve Assembly" },
    dcda: { short: "DCDA", name: "Double Check Detector Assembly" },
    dcda_ii: { short: "DCDA Type II", name: "Double Check Detector Assembly, Type II" },
    rpda: { short: "RPDA", name: "Reduced Pressure Zone Detector Assembly" },
    rpda_ii: { short: "RPDA Type II", name: "Reduced Pressure Zone Detector Assembly, Type II" },
    pvb: { short: "PVB", name: "Pressure Vacuum Breaker" },
    svb: { short: "SVB", name: "Spill-Resistant Vacuum Breaker" },
};

export function assemblyShort(code: string | null | undefined): string {
    const c = (code ?? "").toLowerCase().trim();
    return ASSEMBLY_VOCAB[c]?.short ?? c.toUpperCase();
}

export function assemblyName(code: string | null | undefined): string {
    const c = (code ?? "").toLowerCase().trim();
    return ASSEMBLY_VOCAB[c]?.name ?? assemblyShort(c);
}

/**
 * One formatter for the "Assets" cell everywhere. A single device shows its
 * short code(s) properly cased ("DC", never raw "dc"); multiples show a
 * count; zero returns null so callers render their own em-dash/empty state.
 */
export function formatAssets(count: number | null | undefined, types: string | null | undefined): string | null {
    const n = count ?? 0;
    if (n <= 0) return null;
    if (n === 1) {
        const short = (types ?? "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .map(assemblyShort)
            .join(", ");
        return short || "1 device";
    }
    return `${n} devices`;
}

/**
 * #823 — a device count the title cannot contradict.
 *
 * Job titles are free text and the office habitually types a device count
 * into them ("Mercantile (5) Backflow Test"). Nothing compared that number
 * to the devices actually linked, and the office audit found 4 of ~30 board
 * jobs contradicting — including one titled "(5)" with ZERO devices, which
 * sends a tech out with nothing to test. These helpers derive the truth so
 * every list/detail surface can show it next to the hand-typed claim.
 */

/** First "(N)" pattern in a job title, or null when the title makes no
 *  numeric claim. Matches 1–3 digit parenthesized counts only — WO numbers
 *  and phone fragments are longer and never match. */
export function titleClaimedDeviceCount(title: string | null | undefined): number | null {
    const m = /\((\d{1,3})\)/.exec(title ?? "");
    return m ? Number(m[1]) : null;
}

/** Non-null when the title claims a count that disagrees with the linked
 *  devices. `{ titleSays, attached }` feeds the warning chip verbatim. */
export function deviceCountMismatch(
    title: string | null | undefined,
    assetCount: number | null | undefined,
): { titleSays: number; attached: number } | null {
    const claimed = titleClaimedDeviceCount(title);
    if (claimed === null) return null;
    const attached = assetCount ?? 0;
    return claimed === attached ? null : { titleSays: claimed, attached };
}

/** Trades whose jobs are expected to carry devices — a zero there is a
 *  defect worth warn styling, not an em-dash. */
export function tradeExpectsDevices(tradeName: string | null | undefined): boolean {
    return /backflow|fire/i.test(tradeName ?? "");
}

/**
 * The picker offering — the types office/tech forms let a user choose. Kept
 * separate from ASSEMBLY_VOCAB because legacy "dcva" exists on historical
 * rows (and must render a label) but is not offered on new devices.
 */
export type AssemblyType = "rpz" | "dc" | "dcva" | "dcda" | "dcda_ii" | "rpda" | "rpda_ii" | "pvb" | "svb";

/** "RPZ — Reduced Pressure Zone": the dropdown text a first-day hire can
 *  learn from (UX audit #5 — the Assembly Type picker was the app's
 *  highest-traffic bare-acronym field). Derived from ASSEMBLY_VOCAB so the
 *  short code and the teaching name can never disagree. */
export function assemblyOptionLabel(code: string): string {
    const v = ASSEMBLY_VOCAB[code];
    return v ? `${v.short} — ${v.name}` : code.toUpperCase();
}

export const ASSEMBLY_TYPE_OPTIONS: ReadonlyArray<{ value: AssemblyType; label: string }> = (
    ["rpz", "dc", "dcda", "dcda_ii", "rpda", "rpda_ii", "pvb", "svb"] as const
).map((value) => ({ value, label: assemblyOptionLabel(value) }));

// ─── Imported umbrella contacts (#824) ───────────────────────────────────
// The ServiceTitan import parks un-parseable contact rows under one umbrella
// contact per scope, literally named "ServiceTitan contacts" (server
// authority: stContactsShared.ts ST_UMBRELLA_FIRST_NAME/LAST_NAME — parity
// tested). The office audit caught that string rendering on the job page in
// the same bold style as a real person's name. No surface may present it as
// a person: show UMBRELLA_CONTACT_LABEL in warn styling instead. The data
// cleanup itself is #817's runbook; this is the display truth until then.

export const ST_UMBRELLA_CONTACT_NAME = "ServiceTitan contacts";

/**
 * The umbrella row's name as it will read once the #1243 tier-2 migration
 * renames it. Listed HERE, ahead of that migration, on purpose.
 *
 * The umbrella is detected by matching its name — in two places, this one and
 * `routes/dataQuality.ts`. Rename the rows without teaching the matchers the
 * new name first and both go blind mid-deploy: the umbrella stops reading as
 * an umbrella and starts looking like a real contact, silently. So the matcher
 * accepts BOTH for one release; the migration can then land safely, and the old
 * name can be dropped from this list a release after that.
 */
export const IMPORTED_UMBRELLA_CONTACT_NAME = "Imported contacts";

const UMBRELLA_CONTACT_NAMES = [ST_UMBRELLA_CONTACT_NAME, IMPORTED_UMBRELLA_CONTACT_NAME];

/** What a human sees where the umbrella row would have shown as a name. */
export const UMBRELLA_CONTACT_LABEL = "Imported contact — needs a real name";

export function isUmbrellaContactName(name: string | null | undefined): boolean {
    const norm = (name ?? "").trim().replace(/\s+/g, " ").toLowerCase();
    return UMBRELLA_CONTACT_NAMES.some((n) => norm === n.toLowerCase());
}

/**
 * How to render a contact's name anywhere it is shown AS a name.
 *
 * #824 caught the umbrella rendering in the same bold style as a real person
 * on the job page and fixed it there. It was never applied to the other four
 * surfaces that show contacts — the customer page, the site page, the routing
 * tab and the recipient editor — so it still reads as a person on all of them.
 * One helper so there is no fifth dialect.
 */
export function contactNameDisplay(
    first: string | null | undefined,
    last?: string | null,
): { text: string; isUmbrella: boolean } {
    const joined = [first ?? "", last ?? ""].join(" ").trim().replace(/\s+/g, " ");
    return isUmbrellaContactName(joined)
        ? { text: UMBRELLA_CONTACT_LABEL, isUmbrella: true }
        : { text: joined, isUmbrella: false };
}

// ─── Job detail stage-strip labels ───────────────────────────────────────
// The strip's chapter names (Created → … → Closed). Kept here so the strip
// speaks the same vocabulary as everything else instead of being a fourth
// dialect; the "work" stage's verb stays dynamic (Tested/Repaired/Replaced,
// derived from the job type in jobDetailHelpers.workVerb).

export const JOB_STAGE_LABELS = {
    created: "Created",
    scheduled: "Scheduled",
    on_site: "On site",
    close_out: "Close Out",
    quote_sent: "Quote sent",
    invoiced: "Invoiced",
    paid: "Paid",
    closed: "Closed",
} as const;

// ─── Estimate approval gate ──────────────────────────────────────────────
// Role-based terms, not a person's name: "With Corbin"/"CORBIN GATE" bakes
// today's owner into permanent UI vocabulary, which both confuses new staff
// and blocks the multi-tenant future. Rename here once and every consumer
// follows (adopted by the closeout screens in the audit-remediation PRs).

export const WITH_APPROVER_LABEL = "With owner";
export const APPROVAL_GATE_LABEL = "Owner approval";

// ─── Non-job enum labels (UX comprehension audit, tranche 1) ─────────────
// The job lifecycle got labels above; every other enum in the app rendered
// raw snake_case ("partially_paid", "rma_issued", "auto_idle") in 15+ places.
// These maps' authorities are SQL CHECK constraints in docker/init/ (cited
// per map), not importable server TS, so they are deliberately NOT parity-
// tested — each lookup degrades to replace(/_/g, " ") on an unknown value,
// which is exactly the old behavior at its best.

// Union of the estimates CHECK (docker/init/014_commercial_documents.sql:10)
// and invoices CHECK (:43). The two sets only share "draft"; keeping one map
// means one import at every call site that renders either document kind.
export const COMMERCIAL_DOC_STATUS_LABEL: Record<string, string> = {
    draft: "Draft",
    sent: "Sent",
    accepted: "Accepted",
    declined: "Declined",
    cancelled: "Cancelled",
    open: "Open",
    partially_paid: "Partially paid",
    paid: "Paid",
    void: "Void",
};

export function commercialDocStatusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    return COMMERCIAL_DOC_STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

// docker/init/067_warranties.sql:54 CHECK.
export const WARRANTY_CLAIM_STATUS_LABEL: Record<string, string> = {
    open: "Open",
    in_review: "In review",
    approved: "Approved",
    denied: "Denied",
    rma_issued: "RMA issued",
    received: "Received",
    replaced: "Replaced",
    refunded: "Refunded",
    closed: "Closed",
};

export function warrantyClaimStatusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    return WARRANTY_CLAIM_STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

// Merged, non-colliding union of point_earn_event_status
// (docker/init/001_point_pay_engine.sql:82-87), point_adjustments
// (:139-141), and commission_earnings (docker/init/058_commissions.sql:38).
export const PAYROLL_STATUS_LABEL: Record<string, string> = {
    pending: "Pending",
    provisional: "Provisional",
    approved: "Approved",
    rejected: "Rejected",
    void: "Void",
    paid: "Paid",
    reversed: "Reversed",
};

export function payrollStatusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    return PAYROLL_STATUS_LABEL[s] ?? s.replace(/_/g, " ");
}

// time_clock_entries.source is free VARCHAR(30) DEFAULT 'app'
// (docker/init/036_time_clock_entries.sql:8); 'pto' rows are written by
// routes/pto.ts. The fallback carries anything imports invent.
export const TIMECLOCK_SOURCE_LABEL: Record<string, string> = {
    app: "App",
    web: "Web",
    pto: "PTO",
    auto_idle: "Auto (idle)",
};

export function timeclockSourceLabel(source: string | null | undefined): string {
    const s = (source ?? "").toLowerCase().trim();
    return TIMECLOCK_SOURCE_LABEL[s] ?? s.replace(/_/g, " ");
}

// call_ins.leave_type CHECK (docker/init/255_call_ins.sql:29-30).
export const LEAVE_TYPE_LABEL: Record<string, string> = {
    unpaid: "Unpaid",
    pto: "PTO",
    sick: "Sick",
};

export function leaveTypeLabel(leaveType: string | null | undefined): string {
    const s = (leaveType ?? "").toLowerCase().trim();
    return LEAVE_TYPE_LABEL[s] ?? s.replace(/_/g, " ");
}

// attachments.category is free VARCHAR(30) DEFAULT 'photo'
// (docker/init/040_job_attachments.sql:12). Value set and wording lifted
// from My Uploads' filedAsLabel(), which now delegates here so the gallery
// and My Uploads can never disagree about the same row again.
export const ATTACHMENT_CATEGORY_LABEL: Record<string, string> = {
    photo: "Photo",
    document: "Document",
    report: "Report",
    summary: "Summary",
    test_report: "Test report",
    test_report_official: "Test report",
    work_order_filled: "Work order",
    work_order_uploaded: "Work order",
};

export function attachmentCategoryLabel(category: string | null | undefined): string {
    const s = (category ?? "").toLowerCase().trim();
    return ATTACHMENT_CATEGORY_LABEL[s] ?? s.replace(/_/g, " ");
}
